import { useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'next-i18next'
import { v4 as uuid } from 'uuid'

import ActivityWrapper from '~/bootcamp/Activities/components/ActivityWrapper'
import Equals from '~/bootcamp/Activities/components/Equals'
import Input from '~/bootcamp/Activities/components/Input'
import MultiplicationActivityWrapper from '~/bootcamp/Activities/Multiplication/components/MultiplicationActivityWrapper'
import Times from '~/bootcamp/Activities/components/Times'
import { ActivityProps } from '~/bootcamp/Activities/types/activities.types'
import { randomIntFromInterval, hocOnEnterPress } from '~/bootcamp/Activities/utils/activities.utils'
import { useEmitInstance } from '~/bootcamp/Activities/utils/instance.utils'

import useFocus from '~/bootcamp/Activities/hooks/useFocus'

import { negateAndShuffle, selectRandomItem } from './helper'

const LEVEL1_DIFF1_TERMS = [1, 2, 3, 4, 5, 10]
const DIFFICULTY_LEVELS_TERM_RANGE = {
  2: { min: 1, max: 10 },
  3: { min: 6, max: 12 },
}

export default function OneNegative({
  answerState,
  onAnswerStateChange,
  onInstanceChange,
  difficultyLevel,
  onNextQuestionClick,
  actionButtonsFC,
  autofocus = true,
}: ActivityProps): React.ReactElement {
  const [answer, setAnswer] = useState('')
  const [inputKey, setInputKey] = useState<string>(uuid())
  const [taskHistory, setTaskHistory] = useState<number[]>([])
  const inputRef = useRef<HTMLInputElement>(null)
  const { t } = useTranslation('generics')
  useFocus(autofocus, answerState, inputRef)

  const [term1, term2] = useMemo(() => {
    setAnswer('')
    return generateTerms(difficultyLevel, taskHistory)
  }, [difficultyLevel, taskHistory])

  useEffect(() => {
    onAnswerStateChange(null)
  }, [answer, onAnswerStateChange])

  useEmitInstance(onInstanceChange, `${term1} \\cdot ${term2 < 0 ? `(${term2})` : term2} = \\square`)

  const handleOnCheckAnswerClick = (): void => {
    const isCorrect = parseInt(answer, 10) === term2 * term1
    onAnswerStateChange(isCorrect)
  }

  const handleOnNextQuestionClick = (): void => {
    setTaskHistory((prevState) => {
      return [term1, term2, ...prevState].slice(0, 3)
    })

    onAnswerStateChange(null)
    onNextQuestionClick()
    setInputKey(uuid())
  }
  const onEnterPress = (): void => {
    if (answer !== '') {
      hocOnEnterPress(answerState, handleOnCheckAnswerClick, handleOnNextQuestionClick)
    }
  }

  return (
    <ActivityWrapper>
      <MultiplicationActivityWrapper>
        <span>
          <span>{term1}</span>
          <Times />
          <span>{term2 < 0 ? `(${term2})` : term2}</span>
        </span>
        <span className="block sm:inline-block">
          <Equals />
          <Input
            aria-label={t('ariaLabelButtons.insertProduct')}
            autoFocus={autofocus}
            isError={answerState === false}
            isWide
            key={inputKey}
            maxDigits={4}
            onChange={(value) => setAnswer(value)}
            onEnter={onEnterPress}
            ref={inputRef}
            value={answer}
          />
        </span>
      </MultiplicationActivityWrapper>
      {actionButtonsFC &&
        actionButtonsFC({
          formValid: answer !== '',
          correctAnswer: answerState ?? false,
          submitAnswerAction: handleOnCheckAnswerClick,
          clearFormAction: () => setAnswer(''),
          goToNextQuestionAction: handleOnNextQuestionClick,
        })}
    </ActivityWrapper>
  )
}

/**
 * to do Improve shuffling
 */
function generateTerms(diffLevel: number, taskHistory: number[]): number[] {
  switch (diffLevel) {
    case 2: {
      const term1 = randomIntFromInterval(
        DIFFICULTY_LEVELS_TERM_RANGE[2].min,
        DIFFICULTY_LEVELS_TERM_RANGE[2].max,
        taskHistory,
      )
      const term2 = randomIntFromInterval(
        DIFFICULTY_LEVELS_TERM_RANGE[2].min,
        DIFFICULTY_LEVELS_TERM_RANGE[2].max,
        taskHistory,
      )

      return negateAndShuffle([term1, term2])
    }

    case 3: {
      const term1 = randomIntFromInterval(
        DIFFICULTY_LEVELS_TERM_RANGE[3].min,
        DIFFICULTY_LEVELS_TERM_RANGE[3].max,
        taskHistory,
      )
      const term2 = randomIntFromInterval(
        DIFFICULTY_LEVELS_TERM_RANGE[3].min,
        DIFFICULTY_LEVELS_TERM_RANGE[3].max,
        taskHistory,
      )

      return negateAndShuffle([term1, term2])
    }

    default: {
      const { val: term1 } = selectRandomItem(LEVEL1_DIFF1_TERMS)
      const { val: term2 } = selectRandomItem(LEVEL1_DIFF1_TERMS)

      return negateAndShuffle([term1, term2])
    }
  }
}
